import { createAvatar } from '@dicebear/core';
import * as adventurer from '@dicebear/adventurer';

interface AvatarOptions {
  seed: string;
  hair?: string[];
  hairColor?: string[];
  hairProbability?: number;
  skinColor?: string[];
  features?: string[];
  featuresProbability?: number;
  eyebrows?: string[];
  eyes?: string[];
  mouth?: string[];
  earrings?: string[];
  earringsProbability?: number;
  glasses?: string[];
  glassesProbability?: number;
  backgroundColor?: string[];
}

export interface AvatarPreset {
  id: string;
  label: string;
  options: AvatarOptions;
}

// Hair groups
const SHORT  = ['short01','short03','short05','short07','short09','short11','short13','short15'];
const SHORT2 = ['short02','short04','short06','short08','short10','short12','short14','short16'];
const LONG   = ['long01','long03','long05','long07','long09','long11','long13','long15'];
const LONG2  = ['long02','long04','long06','long08','long10','long12','long14','long16'];

// Skin tones
const S_LYS  = ['f2d3b1'];
const S_ML   = ['ecad80'];
const S_MD   = ['9e5622'];
const S_MORK = ['763900'];

// Hair colors
const H_DARK   = ['0e0e0e','562306','6a4e35'];
const H_BROWN  = ['6a4e35','796a45','562306'];
const H_LIGHT  = ['e5d7a3','b9a05f','796a45'];
const H_BRIGHT = ['3eac2c','85c2c6','dba3be','592454'];
const H_GREY   = ['afafaf','e5d7a3'];

// Friendly mouths only — upward curves, no frowns/scowls
const HAPPY_MOUTHS = ['variant01','variant05','variant11','variant14','variant22','variant23','variant24','variant25'];
// Relaxed eyebrows — no furrowed/angry shapes
const FRIENDLY_BROWS = ['variant05','variant06','variant10','variant11','variant12','variant14','variant15'];

const NO_FEATURES  = { featuresProbability: 0, glassesProbability: 0, earringsProbability: 0, mouth: HAPPY_MOUTHS, eyebrows: FRIENDLY_BROWS };
const WITH_EARRINGS = { featuresProbability: 0, glassesProbability: 0, earringsProbability: 100, mouth: HAPPY_MOUTHS, eyebrows: FRIENDLY_BROWS };

export const DICEBEAR_PRESETS: AvatarPreset[] = [
  // ── Herre ──
  { id: 'mann-lys',    label: 'Herre lys',          options: { seed: 'mann-lys-A',    hair: SHORT,  hairColor: H_DARK,  skinColor: S_LYS,  ...NO_FEATURES } },
  { id: 'mann-ml',     label: 'Herre medium lys',   options: { seed: 'mann-ml-A',     hair: SHORT,  hairColor: H_BROWN, skinColor: S_ML,   ...NO_FEATURES } },
  { id: 'mann-md',     label: 'Herre medium mørk',  options: { seed: 'mann-md-A',     hair: SHORT2, hairColor: H_DARK,  skinColor: S_MD,   ...NO_FEATURES } },
  { id: 'mann-mork',   label: 'Herre mørk',         options: { seed: 'mann-mork-A',   hair: SHORT2, hairColor: H_DARK,  skinColor: S_MORK, ...NO_FEATURES } },

  // ── Dame ──
  { id: 'dame-lys',    label: 'Dame lys',            options: { seed: 'dame-lys-A',    hair: LONG,  hairColor: H_LIGHT,  skinColor: S_LYS,  ...WITH_EARRINGS } },
  { id: 'dame-ml',     label: 'Dame medium lys',     options: { seed: 'dame-ml-A',     hair: LONG,  hairColor: H_BROWN,  skinColor: S_ML,   ...WITH_EARRINGS } },
  { id: 'dame-md',     label: 'Dame medium mørk',    options: { seed: 'dame-md-A',     hair: LONG2, hairColor: H_DARK,   skinColor: S_MD,   ...WITH_EARRINGS } },
  { id: 'dame-mork',   label: 'Dame mørk',           options: { seed: 'dame-mork-A',   hair: LONG2, hairColor: H_DARK,   skinColor: S_MORK, ...WITH_EARRINGS } },

  // ── Gutt ──
  { id: 'gutt-lys',    label: 'Gutt lys',            options: { seed: 'gutt-lys-B',    hair: SHORT2, hairColor: H_LIGHT, skinColor: S_LYS,  ...NO_FEATURES } },
  { id: 'gutt-ml',     label: 'Gutt medium lys',     options: { seed: 'gutt-ml-B',     hair: SHORT2, hairColor: H_BROWN, skinColor: S_ML,   ...NO_FEATURES } },
  { id: 'gutt-md',     label: 'Gutt medium mørk',    options: { seed: 'gutt-md-B',     hair: SHORT,  hairColor: H_DARK,  skinColor: S_MD,   ...NO_FEATURES } },
  { id: 'gutt-mork',   label: 'Gutt mørk',           options: { seed: 'gutt-mork-B',   hair: SHORT,  hairColor: H_DARK,  skinColor: S_MORK, ...NO_FEATURES } },

  // ── Jente ──
  { id: 'jente-lys',   label: 'Jente lys',           options: { seed: 'jente-lys-C',   hair: LONG2, hairColor: H_BRIGHT, skinColor: S_LYS,  ...NO_FEATURES } },
  { id: 'jente-ml',    label: 'Jente medium lys',    options: { seed: 'jente-ml-C',    hair: LONG2, hairColor: H_LIGHT,  skinColor: S_ML,   ...NO_FEATURES } },
  { id: 'jente-md',    label: 'Jente medium mørk',   options: { seed: 'jente-md-C',    hair: LONG,  hairColor: H_BROWN,  skinColor: S_MD,   ...NO_FEATURES } },
  { id: 'jente-mork',  label: 'Jente mørk',          options: { seed: 'jente-mork-C',  hair: LONG,  hairColor: H_DARK,   skinColor: S_MORK, ...NO_FEATURES } },

  // ── Eldre ──
  { id: 'eldre-lys',   label: 'Eldre lys',   options: { seed: 'eldre-lys-D',   hair: SHORT,  hairColor: H_GREY, skinColor: S_LYS,  featuresProbability: 100, glassesProbability: 80, earringsProbability: 0, mouth: HAPPY_MOUTHS, eyebrows: FRIENDLY_BROWS } },
  { id: 'eldre-mork',  label: 'Eldre mørk',  options: { seed: 'eldre-mork-D',  hair: SHORT,  hairColor: H_GREY, skinColor: S_MORK, featuresProbability: 100, glassesProbability: 80, earringsProbability: 0, mouth: HAPPY_MOUTHS, eyebrows: FRIENDLY_BROWS } },
  { id: 'dame-brille', label: 'Dame m/ briller', options: { seed: 'dame-brille-E', hair: LONG, hairColor: H_LIGHT, skinColor: S_ML, featuresProbability: 0, glassesProbability: 100, earringsProbability: 100, mouth: HAPPY_MOUTHS, eyebrows: FRIENDLY_BROWS } },
  { id: 'mann-brille', label: 'Herre m/ briller', options: { seed: 'mann-brille-E', hair: SHORT, hairColor: H_DARK, skinColor: S_LYS, featuresProbability: 0, glassesProbability: 100, earringsProbability: 0, mouth: HAPPY_MOUTHS, eyebrows: FRIENDLY_BROWS } },
];

// Quick picks shown without expanding
export const QUICK_PICK_IDS = ['mann-lys','mann-mork','dame-lys','dame-mork','gutt-lys','gutt-mork','jente-lys','jente-mork'];

// Category grouping for expanded view
export const DICEBEAR_CATEGORIES = [
  { label: 'Herre',  ids: ['mann-lys','mann-ml','mann-md','mann-mork','mann-brille'] },
  { label: 'Dame',   ids: ['dame-lys','dame-ml','dame-md','dame-mork','dame-brille'] },
  { label: 'Gutt',   ids: ['gutt-lys','gutt-ml','gutt-md','gutt-mork'] },
  { label: 'Jente',  ids: ['jente-lys','jente-ml','jente-md','jente-mork'] },
  { label: 'Eldre',  ids: ['eldre-lys','eldre-mork'] },
];

const _cache = new Map<string, string>();

export function getAvatarDataUri(presetId: string): string {
  if (_cache.has(presetId)) return _cache.get(presetId)!;
  const preset = DICEBEAR_PRESETS.find(p => p.id === presetId);
  if (!preset) return '';
  const uri = (createAvatar as any)(adventurer, preset.options).toDataUri() as string;
  _cache.set(presetId, uri);
  return uri;
}
